import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Crown, ArrowDown, UserCheck, Sparkles, Users, Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Family tree panel — shows the recruitment lineage:
 *   - PADRINHO/MADRINHA at top (who recruited this player)
 *   - YOU in the middle
 *   - AFILHADOS at the bottom (who YOU recruited)
 *
 * Visual badge "Padrinho Dourado" + bonus +50 pts when afilhados_count >= 3.
 */
export default function FamilyTreePanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/my-profile/family").then((r) => setData(r.data)).catch((e) => {
      console.debug("family fetch failed:", e?.message);
    });
  }, []);

  if (!data) return null;
  const { padrinho, afilhados, afilhados_count, golden_padrinho, golden_bonus_pts, me } = data;

  return (
    <div className="kw-card p-6" data-testid="family-tree-panel">
      <SectionHeader golden={golden_padrinho} goldenPts={golden_bonus_pts} />
      {padrinho && (
        <>
          <PersonCard person={padrinho} label="Foste recomendado por" highlight />
          <DownArrow />
        </>
      )}
      <MeCard me={me} afilhadosCount={afilhados_count} />
      <SharePanel meId={me.id} />
      {afilhados_count > 0 && (
        <>
          <DownArrow />
          <div className="grid sm:grid-cols-2 gap-2">
            {afilhados.map((af) => (
              <PersonCard key={af.id} person={af} compact
                tag={af.has_reported ? "+20 pts creditados" : "Aguarda 1.º reporting"}
                okTag={af.has_reported} />
            ))}
          </div>
        </>
      )}
      {!golden_padrinho && (
        <p className="text-[10px] text-[#737373] uppercase tracking-widest mt-4 text-center">
          Convida mais {3 - afilhados_count} colega{3 - afilhados_count === 1 ? "" : "s"} para te tornares <span className="text-[#EAB308] font-bold">Padrinho Dourado</span> (+50 pts).
        </p>
      )}
    </div>
  );
}

function SectionHeader({ golden, goldenPts }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-4 border-b border-[#222] pb-3">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-[#E50000]" />
        <div>
          <div className="kw-overline">Árvore da Liga</div>
          <h2 className="font-display text-2xl font-black uppercase">Padrinhos &amp; Afilhados</h2>
        </div>
      </div>
      {golden && (
        <div className="flex items-center gap-2 border border-[#EAB308] bg-[#EAB308]/10 px-3 py-1.5" data-testid="golden-padrinho-badge">
          <Crown className="w-4 h-4 text-[#EAB308]" />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#EAB308]">Padrinho Dourado</div>
            <div className="text-[10px] text-[#A3A3A3]">+{goldenPts} pts bónus</div>
          </div>
        </div>
      )}
    </div>
  );
}

function DownArrow() {
  return (
    <div className="flex justify-center my-3">
      <ArrowDown className="w-5 h-5 text-[#737373]" />
    </div>
  );
}

function MeCard({ me, afilhadosCount }) {
  return (
    <div className="border border-[#E50000]/40 bg-[#E50000]/5 p-3 flex items-center gap-3" data-testid="me-card">
      <div className="w-10 h-10 rounded-full bg-[#E50000]/30 flex items-center justify-center">
        <Sparkles className="w-5 h-5 text-[#E50000]" />
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#E50000]">Tu</div>
        <div className="font-display font-black uppercase tracking-tight">{me.name}</div>
      </div>
      <div className="text-right">
        <div className="font-display text-2xl font-black" data-testid="afilhados-count">{afilhadosCount}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">afilhado{afilhadosCount === 1 ? "" : "s"}</div>
      </div>
    </div>
  );
}

function SharePanel({ meId }) {
  const share = async () => {
    const url = `${window.location.origin}/voice/${meId}`;
    try {
      if (navigator.share) { await navigator.share({ url, title: "KW Lead Growth League" }); }
      else { await navigator.clipboard.writeText(url); toast.success("Link copiado."); }
    } catch (e) {
      if (e?.name !== "AbortError") console.debug("share failed:", e?.message);
    }
  };
  return (
    <div className="mt-3 border border-[#222] p-3 flex flex-wrap items-center justify-between gap-3" data-testid="share-my-landing">
      <div className="flex items-center gap-2 text-xs text-[#A3A3A3]">
        <Share2 className="w-4 h-4 text-[#3B82F6]" />
        <span>A tua página pública: <code className="text-white text-[11px]">/voice/{meId}</code></span>
      </div>
      <div className="flex gap-2">
        <a href={`/voice/${meId}`} target="_blank" rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white border border-[#222] hover:border-[#444] px-3 py-1.5"
          data-testid="my-landing-open">Ver</a>
        <button onClick={share}
          className="text-[10px] uppercase tracking-widest font-bold text-white bg-[#3B82F6] hover:bg-[#2563EB] px-3 py-1.5"
          data-testid="my-landing-share">Partilhar</button>
      </div>
    </div>
  );
}

function PersonCard({ person, label, highlight, compact, tag, okTag }) {
  return (
    <div className={`border p-3 flex items-center gap-3 ${highlight ? "border-[#E50000]/40 bg-[#E50000]/5" : "border-[#222]"}`} data-testid={`family-person-${person.id}`}>
      {person.photo_url ? (
        <img src={person.photo_url} alt={person.name} className={`${compact ? "w-9 h-9" : "w-12 h-12"} rounded-full object-cover`} />
      ) : (
        <div className={`${compact ? "w-9 h-9 text-xs" : "w-12 h-12 text-sm"} rounded-full bg-[#1A1A1A] flex items-center justify-center font-bold text-[#737373]`}>
          {(person.name || "?").slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {label && <div className="text-[10px] uppercase tracking-widest font-bold text-[#E50000]">{label}</div>}
        <div className={`font-display ${compact ? "text-sm" : "text-lg"} font-black uppercase tracking-tight truncate`}>{person.name}</div>
        <div className="text-[10px] text-[#737373] uppercase tracking-widest">
          {person.club || "—"} {person.mbti ? `· ${person.mbti}` : ""}
        </div>
      </div>
      {tag && (
        <div className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 border ${okTag ? "border-[#22C55E]/40 text-[#22C55E]" : "border-[#FFB020]/40 text-[#FFB020]"}`}>
          {okTag ? <UserCheck className="w-3 h-3 inline mr-1" /> : null}
          {tag}
        </div>
      )}
    </div>
  );
}
